/**
 * LoginPage
 * 
 * Full-screen login page with Auth0 integration.
 * Supports Google, Microsoft/Outlook, and email/password authentication.
 */

import React from 'react';
import { useAuth } from '../../context';
import { Logo } from '../../components/common/Logo';
import styles from './LoginPage.module.css';

// Inline SVG icons for social providers
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const MicrosoftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

export const LoginPage: React.FC = () => {
  const { isLoading, login, loginWithGoogle, loginWithMicrosoft, signup } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.loadingOverlay}>
        <div className={styles.spinner} />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        {/* Brand */}
        <div className={styles.brandSection}>
          <div className={styles.brandLogo}>
            <Logo size="md" />
            <span className={styles.brandName}>BrainIQ</span>
          </div>
          <p className={styles.brandTagline}>
            Enterprise SDLC Agentic Platform — AI-powered agents for every phase of development.
          </p>
        </div>

        {/* Social Login Buttons */}
        <div className={styles.socialButtons}>
          <button
            type="button"
            className={styles.socialButton}
            onClick={loginWithGoogle}
          >
            <GoogleIcon className={styles.socialIcon} />
            Continue with Google
          </button>

          <button
            type="button"
            className={styles.socialButton}
            onClick={loginWithMicrosoft}
          >
            <MicrosoftIcon className={styles.socialIcon} />
            Continue with Microsoft
          </button>
        </div>

        <div className={styles.divider}>or</div>

        {/* Email Login */}
        <button
          type="button"
          className={styles.emailButton}
          onClick={() => login()}
        >
          Sign in with Email
        </button>

        {/* Sign Up */}
        <div className={styles.signupRow}>
          Don&apos;t have an account?{' '}
          <button type="button" className={styles.signupLink} onClick={signup}>
            Sign up
          </button>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          By continuing, you agree to BringIQ&apos;s Terms of Service and Privacy Policy.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
